// PaymentScheduleSection — FP only. Renders milestones + per-milestone
// allocations (lazy on row expansion per F-002 rationale). Schedule
// total renders the BR-06 delta.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { milestonesApi, allocationsApi, peopleApi } from '@/api/endpoints';
import { qk } from '@/api/queryKeys';
import {
  EmptyState,
  ErrorBanner,
  Skeleton,
} from './primitives';
import {
  formatCalendarDate,
  formatCurrencyString,
} from '@/utils/format';
import { ApiError } from '@/types/errors';
import { canEditScheduleOn } from '@/auth/permissions';
import type { Agreement, Allocation, Milestone, Person, Principal } from '@/types/domain';
import { ConfirmationDialog } from './ConfirmationDialog';
import { useToast } from './ToastHost';
import { MilestoneEditor } from '@/forms/MilestoneEditor';
import { AllocationEditor } from '@/forms/AllocationEditor';

interface Props {
  agreement: Agreement;
  principal: Principal | null;
}

// String-decimal subtraction (cents) — rough but good enough at V1 scale.
function diffCurrency(a: string | null, b: string): string {
  const av = Number((a ?? '0').replace(',', ''));
  const bv = Number(b.replace(',', ''));
  const d = av - bv;
  const sign = d < 0 ? '-' : '';
  const abs = Math.abs(d);
  return `${sign}${abs.toFixed(2)}`;
}
function sumAmounts(rows: { amount: string }[]): string {
  let cents = 0;
  for (const r of rows) {
    const n = Number(r.amount.replace(',', '')) * 100;
    if (Number.isFinite(n)) cents += Math.round(n);
  }
  const dollars = cents / 100;
  return dollars.toFixed(2);
}

export function PaymentScheduleSection({ agreement, principal }: Props) {
  const qc = useQueryClient();
  const toast = useToast();
  if (agreement.type !== 'fixed_price') {
    return (
      <section className="section" aria-labelledby="ps-h2">
        <div className="section-header">
          <h2 id="ps-h2">Payment schedule</h2>
        </div>
        <p className="muted">
          Payment Schedule does not apply to T&amp;M agreements.
        </p>
      </section>
    );
  }

  const editGate = canEditScheduleOn(principal, agreement);

  const milestonesQ = useQuery({
    queryKey: qk.agreementMilestones(agreement.id),
    queryFn: () => milestonesApi.listByAgreement(agreement.id),
  });
  const milestones = milestonesQ.data?.data ?? [];

  const total = sumAmounts(milestones);
  const diff = agreement.total_value
    ? diffCurrency(agreement.total_value, total)
    : '0.00';
  const diffNumeric = Number(diff);
  const outOfTolerance =
    agreement.status === 'draft' && Math.abs(diffNumeric) > 100;

  const [editing, setEditing] = useState<
    | { mode: 'create-milestone' }
    | { mode: 'edit-milestone'; milestone: Milestone }
    | null
  >(null);
  const [deleting, setDeleting] = useState<Milestone | null>(null);
  const deleteM = useMutation({
    mutationFn: (id: string) => milestonesApi.delete(id),
  });
  const performDelete = async () => {
    if (!deleting) return;
    try {
      await deleteM.mutateAsync(deleting.id);
      void qc.invalidateQueries({
        queryKey: qk.agreementMilestones(agreement.id),
      });
      toast.show({ severity: 'success', message: 'Milestone deleted.' });
      setDeleting(null);
    } catch (err) {
      toast.show({
        severity: 'error',
        message:
          err instanceof ApiError ? err.message : "Couldn't delete milestone.",
      });
      setDeleting(null);
    }
  };

  return (
    <section className="section" aria-labelledby="ps-h2">
      <div className="section-header">
        <h2 id="ps-h2">Payment schedule</h2>
        {editGate.rendering === 'visible' ? (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditing({ mode: 'create-milestone' })}
          >
            Add milestone
          </button>
        ) : null}
      </div>

      {milestonesQ.isPending ? (
        <Skeleton rows={2} height="32px" />
      ) : milestonesQ.isError ? (
        <ErrorBanner
          message="Couldn't load the payment schedule."
          requestId={
            milestonesQ.error instanceof ApiError ? milestonesQ.error.requestId : undefined
          }
          onRetry={() => void milestonesQ.refetch()}
        />
      ) : milestones.length === 0 ? (
        editGate.rendering === 'visible' ? (
          <EmptyState
            message="No milestones yet."
            action={
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setEditing({ mode: 'create-milestone' })}
              >
                Add milestone
              </button>
            }
          />
        ) : (
          <EmptyState message="No milestones on this schedule." />
        )
      ) : (
        <>
          <table className="data-table">
            <caption className="visually-hidden">Milestones</caption>
            <thead>
              <tr>
                <th scope="col">Milestone</th>
                <th scope="col">Expected date</th>
                <th scope="col">Amount</th>
                <th scope="col">Allocations</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {milestones.map((m) => (
                <MilestoneRow
                  key={m.id}
                  milestone={m}
                  agreement={agreement}
                  principal={principal}
                  onEdit={() =>
                    setEditing({ mode: 'edit-milestone', milestone: m })
                  }
                  onDelete={() => setDeleting(m)}
                />
              ))}
              <tr>
                <td colSpan={2} style={{ textAlign: 'right' }}>
                  <strong>Schedule total</strong>
                </td>
                <td>
                  <strong>{formatCurrencyString(total)}</strong>
                </td>
                <td colSpan={2}>
                  {outOfTolerance ? (
                    <span className="credit-badge credit-badge-incomplete">
                      Δ {formatCurrencyString(diff)} from Total Value (must be ≤ $100 to activate)
                    </span>
                  ) : agreement.status === 'draft' ? (
                    <span className="muted">
                      Within $100 of Total Value.
                    </span>
                  ) : null}
                </td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {editing ? (
        <MilestoneEditor
          open={true}
          onClose={() => setEditing(null)}
          agreement={agreement}
          milestone={
            editing.mode === 'edit-milestone' ? editing.milestone : null
          }
        />
      ) : null}
      {deleting ? (
        <ConfirmationDialog
          open={true}
          title="Delete this milestone?"
          body={`Removing "${deleting.milestone_name}" will also remove every allocation under it.`}
          confirmLabel="Delete milestone"
          confirmIntent="destructive"
          onConfirm={performDelete}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </section>
  );
}

interface RowProps {
  milestone: Milestone;
  agreement: Agreement;
  principal: Principal | null;
  onEdit: () => void;
  onDelete: () => void;
}

function MilestoneRow({ milestone, agreement, principal, onEdit, onDelete }: RowProps) {
  const [expanded, setExpanded] = useState(false);
  const editGate = canEditScheduleOn(principal, agreement);
  return (
    <>
      <tr>
        <th scope="row">{milestone.milestone_name}</th>
        <td>{formatCalendarDate(milestone.expected_date)}</td>
        <td>{formatCurrencyString(milestone.amount)}</td>
        <td>
          <button
            type="button"
            className="btn btn-link"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded ? 'true' : 'false'}
            aria-controls={`alloc-${milestone.id}`}
          >
            {expanded ? 'Hide allocations' : 'Show allocations'}
          </button>
        </td>
        <td>
          {editGate.rendering === 'visible' ? (
            <>
              <button type="button" className="btn btn-link" onClick={onEdit}>
                Edit
              </button>{' '}
              <button
                type="button"
                className="btn btn-link"
                onClick={onDelete}
                style={{ color: 'var(--c-danger)' }}
              >
                Delete
              </button>
            </>
          ) : null}
        </td>
      </tr>
      {expanded ? (
        <tr id={`alloc-${milestone.id}`}>
          <td colSpan={5}>
            <AllocationsForMilestone milestone={milestone} agreement={agreement} principal={principal} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AllocationsForMilestone({
  milestone,
  agreement,
  principal,
}: {
  milestone: Milestone;
  agreement: Agreement;
  principal: Principal | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const editGate = canEditScheduleOn(principal, agreement);
  const allocQ = useQuery({
    queryKey: qk.milestoneAllocations(milestone.id),
    queryFn: () => allocationsApi.listByMilestone(milestone.id),
  });
  const peopleQ = useQuery({
    queryKey: qk.peopleAll(),
    queryFn: () => peopleApi.list({ limit: 100 }),
    staleTime: 60_000,
  });
  const peopleById = useMemo(() => {
    const m = new Map<string, Person>();
    for (const p of peopleQ.data?.data ?? []) m.set(p.id, p);
    return m;
  }, [peopleQ.data]);

  const [editing, setEditing] = useState<
    | { mode: 'create' }
    | { mode: 'edit'; allocation: Allocation }
    | null
  >(null);
  const [deleting, setDeleting] = useState<Allocation | null>(null);
  const deleteM = useMutation({
    mutationFn: (id: string) => allocationsApi.delete(id),
  });

  const allocations = allocQ.data?.data ?? [];
  const sum = sumAmounts(allocations);
  const remaining = diffCurrency(milestone.amount, sum);
  const remainingNum = Number(remaining);
  const balanced = Math.abs(remainingNum) < 0.005;

  const performDelete = async () => {
    if (!deleting) return;
    try {
      await deleteM.mutateAsync(deleting.id);
      void qc.invalidateQueries({
        queryKey: qk.milestoneAllocations(milestone.id),
      });
      toast.show({ severity: 'success', message: 'Allocation deleted.' });
    } catch (err) {
      toast.show({
        severity: 'error',
        message:
          err instanceof ApiError ? err.message : "Couldn't delete allocation.",
      });
    } finally {
      setDeleting(null);
    }
  };

  if (allocQ.isPending) return <Skeleton rows={2} />;
  if (allocQ.isError) {
    return (
      <ErrorBanner
        message="Couldn't load allocations."
        onRetry={() => void allocQ.refetch()}
      />
    );
  }

  return (
    <div style={{ paddingLeft: 16 }}>
      <p className="muted">
        Allocated: {formatCurrencyString(sum)} of{' '}
        {formatCurrencyString(milestone.amount)}{' '}
        {balanced ? '(balanced)' : `(${formatCurrencyString(remaining)} remaining)`}
      </p>
      {allocations.length === 0 ? (
        <p className="muted">No allocations yet.</p>
      ) : (
        <ul>
          {allocations.map((a) => (
            <li key={a.id}>
              {peopleById.get(a.person_id)?.name ?? a.person_id}:{' '}
              {formatCurrencyString(a.amount)}{' '}
              {editGate.rendering === 'visible' ? (
                <>
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() =>
                      setEditing({ mode: 'edit', allocation: a })
                    }
                  >
                    Edit
                  </button>{' '}
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => setDeleting(a)}
                    style={{ color: 'var(--c-danger)' }}
                  >
                    Delete
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {editGate.rendering === 'visible' ? (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setEditing({ mode: 'create' })}
        >
          Add allocation
        </button>
      ) : null}

      {editing ? (
        <AllocationEditor
          open={true}
          onClose={() => setEditing(null)}
          milestone={milestone}
          allocation={
            editing.mode === 'edit' ? editing.allocation : null
          }
        />
      ) : null}
      {deleting ? (
        <ConfirmationDialog
          open={true}
          title="Delete this allocation?"
          body="The total of remaining allocations must still equal the milestone amount, or the delete will be rejected."
          confirmLabel="Delete allocation"
          confirmIntent="destructive"
          onConfirm={performDelete}
          onCancel={() => setDeleting(null)}
        />
      ) : null}
    </div>
  );
}
